import { faUser, faUsers, faTimes } from '@fortawesome/free-solid-svg-icons';
import { isOutOfOffice } from './outOfOffice.js';

/**
 * Resolve assignee name from ID (checks both users and teams)
 * @param {string} assigneeId - The assignee ID to resolve
 * @param {Array} users - Array of user objects
 * @param {Array} teams - Array of team objects
 * @returns {string} The resolved assignee name
 */
export const getAssigneeName = (assigneeId, users = [], teams = []) => {
  if (!assigneeId) return 'Unassigned';

  const user = users.find(u => u.userId === assigneeId);
  if (user) return user.name;

  const team = teams.find(t => t.teamId === assigneeId);
  if (team) return team.teamName;

  return 'Unknown';
};

/**
 * Create assignee options with icons for dropdowns
 * @param {Array} users - Array of user objects
 * @param {Array} teams - Array of team objects
 * @param {Object} counts - Optional map of assigneeId -> count (null key for unassigned)
 * @returns {Array} Formatted options array for dropdown
 */
export const createAssigneeOptions = (users = [], teams = [], counts = null) => {
  const unassignedOption = {
    id: 'unassigned',
    userId: null,
    name: 'Unassigned',
    display: 'Unassigned',
    icon: faTimes,
    isTeam: false
  };
  
  const teamOptions = teams.map(team => {
    const count = counts ? (counts[team.teamId] || 0) : null;
    const display = count !== null ? `${team.teamName} (${count})` : team.teamName;
    
    return {
      id: team.teamId,
      userId: team.teamId, // For compatibility with existing code
      name: team.teamName,
      display: display,
      icon: faUsers,
      isTeam: true
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
  
  const userOptions = users.map(user => {
    const count = counts ? (counts[user.userId] || 0) : null;
    const display = count !== null ? `${user.name} (${count})` : user.name;
    
    return {
      id: user.userId,
      userId: user.userId,
      name: user.name,
      display: display,
      icon: faUser,
      isTeam: false,
      outOfOffice: isOutOfOffice(user) ? user.outOfOffice : null
    };
  }).sort((a, b) => {
    const awayDelta = (a.outOfOffice ? 1 : 0) - (b.outOfOffice ? 1 : 0);
    if (awayDelta !== 0) return awayDelta;
    return a.name.localeCompare(b.name);
  });

  // Unassigned, teams, in-office users, then out-of-office users.
  return [unassignedOption, ...teamOptions, ...userOptions];
};

/**
 * Get icon for assignee based on ID
 * @param {string} assigneeId - The assignee ID
 * @param {Array} users - Array of user objects
 * @param {Array} teams - Array of team objects
 * @returns {object} FontAwesome icon object
 */
export const getAssigneeIcon = (assigneeId, users = [], teams = []) => {
  if (!assigneeId) return faTimes;
  
  const team = teams.find(t => t.teamId === assigneeId);
  if (team) return faUsers;
  
  return faUser;
};

